import type { PropertyValues } from 'lit'
import type Calendar from '../calendar/Calendar.js'
import type { DatePredicate } from '../calendar/Calendar.js'
import type { DateRangeSelectEvent } from '../calendar/DateRangeSelectEvent.js'

import * as calendarIcon from '@nordhealth/icons/lib/assets/interface-calendar.js'
import * as closeIcon from '@nordhealth/icons/lib/assets/interface-close-small.js'
import * as dropdownIcon from '@nordhealth/icons/lib/assets/interface-dropdown-small.js'
import { html, LitElement, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { ref } from 'lit/directives/ref.js'

import { OpenStateController } from '../common/controllers/OpenStateController.js'
import { addDays, addMonths, DaysOfWeek, getQuarterRange, isQuarterCompleted, parseISODate, printISODate, setYear, startOfMonth, startOfQuarter, startOfWeek, startOfYear } from '../common/dates.js'
import { observe } from '../common/decorators/observe.js'
import { cond } from '../common/directives/cond.js'
import { NordEvent } from '../common/events.js'
import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import { FocusableMixin } from '../common/mixins/FocusableMixin.js'
import { FormAssociatedMixin } from '../common/mixins/FormAssociatedMixin.js'
import { InputMixin } from '../common/mixins/InputMixin.js'
import { ReadonlyMixin } from '../common/mixins/ReadonlyMixin.js'
import { SizeMixin } from '../common/mixins/SizeMixin.js'
import Icon from '../icon/Icon.js'
import { LocalizeController } from '../localization/LocalizeController.js'
import { DateRangeChangeEvent } from './DateRangeChangeEvent.js'
import style from './DateRangePicker.css'
import '../button/Button.js'
import '../input/Input.js'
import '../calendar/Calendar.js'
import '../popout/Popout.js'
import '../stack/Stack.js'
import '../visually-hidden/VisuallyHidden.js'

Icon.registerIcon(calendarIcon)
Icon.registerIcon(closeIcon)
Icon.registerIcon(dropdownIcon)

/** Per-instance counter so light-DOM ids stay unique across instances. */
let dateRangePickerCount = 1

/** Default calendar predicates: nothing disabled, nothing highlighted. */
const isDateDisabled: DatePredicate = () => false
const isDateHighlighted = () => false

/** A built-in preset row, resolved against "today" each time the popout opens. */
interface Preset {
  /** Stable key for tracking the active preset, and for the `presets` subset. */
  id: string
  /** Which divider group this preset belongs to (presets are grouped in order). */
  group: string
  /** Localized, human-readable label. */
  label: string
  /** Resolves the preset to an inclusive `[start, end]` relative to `today`. */
  resolve: (today: Date) => [Date, Date]
  /**
   * Optional gate (e.g. hide a current-year quarter that hasn't finished yet).
   * When omitted the preset is always available.
   */
  available?: (today: Date) => boolean
}

/** Order of the divider groups in the rendered preset list. */
const PRESET_GROUP_ORDER = ['today', 'lastDays', 'lastPeriod', 'currentPeriod', 'quarters'] as const

/** Splits an interval string `YYYY-MM-DD/YYYY-MM-DD` into its two ISO parts. */
function splitInterval(value: string): [string, string] {
  if (!value) {
    return ['', '']
  }
  const [start = '', end = ''] = value.split('/')
  return [start, end]
}

/** Joins two ISO parts into an interval string; returns `""` if either is missing. */
function joinInterval(start: string, end: string): string {
  return start && end ? `${start}/${end}` : ''
}

/**
 * Date Range Picker lets people choose a start and end date as a single range,
 * either from a built-in list of presets (Today, Last 7 days, This month, …) or
 * from a range calendar. It is a control only — compose it inside a
 * [Field](/components/field/) for a label, hint or error, like a native input.
 *
 * The committed value is an ISO-8601 interval string `YYYY-MM-DD/YYYY-MM-DD`;
 * the convenience `start-date` / `end-date` props and the `valueAsDateRange`
 * getter/setter stay in sync with it. Selecting in the popout builds a *pending*
 * range; **Apply** commits it (firing `change`), **Cancel** reverts to the last
 * committed value, and **Clear** empties it.
 *
 * @status draft
 * @category form
 * @lightDom true
 *
 * @fires {DateRangeChangeEvent} change - Dispatched when the committed range changes (Apply or Clear).
 * @fires {DateRangeChangeEvent} input - Dispatched live as the pending range changes inside the popout.
 * @fires {NordEvent} open - Dispatched when the popout is opened.
 * @fires {NordEvent} close - Dispatched when the popout is closed.
 *
 * @slot trigger - Optional custom trigger element (e.g. a filter chip or a `nord-button`) that opens the picker. Replaces the built-in trigger button when provided.
 *
 * @localization buttonLabel - Accessible label for the trigger button.
 * @localization placeholder - Placeholder shown in the trigger when no range is selected.
 * @localization modalHeading - Heading shown above the calendar in the popout.
 * @localization closeLabel - Accessible label for the close button.
 * @localization applyLabel - Label for the Apply button.
 * @localization cancelLabel - Label for the Cancel button.
 * @localization clearLabel - Label for the Clear button.
 * @localization presetsLabel - Accessible label for the preset list.
 * @localization presetsCollapsedLabel - Label for the collapsed preset dropdown (narrow widths).
 * @localization rangeSeparator - Character shown between the start and end dates in the trigger.
 * @localization dateOutOfMinBound - Error message shown when the range starts before the minimum date.
 * @localization dateOutOfMaxBound - Error message shown when the range ends after the maximum date.
 * @localization presetToday - Label for the "Today" preset.
 * @localization presetYesterday - Label for the "Yesterday" preset.
 * @localization presetLastDays - Label for the "Last X days" presets.
 * @localization presetLastMonths - Label for the "Last X months" presets.
 * @localization presetThisMonth - Label for the "This month" preset.
 * @localization presetLastWeek - Label for the "Last week" preset.
 * @localization presetLastMonth - Label for the "Last month" preset.
 * @localization presetLastQuarter - Label for the "Last quarter" preset.
 * @localization presetLastYear - Label for the "Last year" preset.
 * @localization presetCurrentWeek - Label for the "Current week" preset.
 * @localization presetCurrentMonth - Label for the "Current month" preset.
 * @localization presetCurrentQuarter - Label for the "Current quarter" preset.
 * @localization presetCurrentYear - Label for the "Current year" preset.
 * @localization presetQuarter - Label for a specific calendar quarter, e.g. "Q3 2025".
 *
 * @cssprop [--n-date-range-picker-trigger-min-inline-size=14rem] - Controls the minimum inline size of the trigger.
 * @cssprop [--n-date-range-picker-popout-inline-size] - Controls the inline size of the popout content.
 */
@customElement('nord-date-range-picker')
export default class DateRangePicker extends DraftComponentMixin(
  SizeMixin(FormAssociatedMixin(ReadonlyMixin(InputMixin(FocusableMixin(LitElement))))),
) {
  /** Stable, unique id base for the light-DOM ARIA wiring of this instance. */
  private dateRangePickerId = `nord-date-range-picker-${dateRangePickerCount++}`

  // The built-in `<label for>` (from FieldControlMixin) must point at the real
  // focusable trigger, so override the mixin's default id with the trigger's id.
  protected override inputId = `${this.dateRangePickerId}-input`

  // The trigger is a button that opens the popup on click, so a label click
  // should focus it (with the ring) rather than open it — keyboard opens it.
  protected override get labelFocusOnly() {
    return true
  }

  @query('nord-calendar', true) private calendar?: Calendar
  @query('.n-drp-toggle', true) private toggle?: HTMLElement
  @query('nord-popout', true) private popout?: HTMLElement & {
    show: () => Promise<unknown>
    hide: (moveFocusToButton?: boolean) => Promise<unknown>
  }

  private localize = new LocalizeController<'nord-date-range-picker'>(this, {
    onLangChange: () => this.createDateFormatters(),
  })

  // Coordinates `open` with the popout: `open` is the source of truth (it drives
  // popout.show()/hide()), the popout's own events sync `open` back, and one
  // canonical open/close is emitted. Opening is rejected while disabled/read-only.
  private openState = new OpenStateController(this, {
    isOpen: () => this.open,
    setOpen: (open) => { this.open = open },
    popout: () => this.popout,
    isInert: () => this.disabled || this.readonly,
    onOpen: () => {
      // Seed the pending selection from the committed value on every open, so an
      // abandoned (cancelled) pending range never leaks across opens, then move
      // focus into the calendar.
      this.pendingStart = this.startDate
      this.pendingEnd = this.endDate
      void this.calendar?.updateComplete.then(() => this.calendar?.focus({ target: 'day' }))
    },
  })

  private dateFormat!: Intl.DateTimeFormat

  /**
   * The committed value as an ISO-8601 interval string `YYYY-MM-DD/YYYY-MM-DD`.
   * An empty string means no range is committed.
   */
  @property() value = ''

  /**
   * Convenience accessor for the committed start date, in ISO-8601 format. Kept
   * in sync with `value` and `end-date`.
   */
  @property({ reflect: true, attribute: 'start-date' }) startDate = ''

  /**
   * Convenience accessor for the committed end date, in ISO-8601 format. Kept in
   * sync with `value` and `start-date`.
   */
  @property({ reflect: true, attribute: 'end-date' }) endDate = ''

  /**
   * Controls whether the popout is open.
   */
  @property({ type: Boolean, reflect: true }) open = false

  /**
   * Minimum date allowed to be picked, in ISO-8601 format: YYYY-MM-DD.
   */
  @property({ reflect: true }) min?: string

  /**
   * Maximum date allowed to be picked, in ISO-8601 format: YYYY-MM-DD.
   */
  @property({ reflect: true }) max?: string

  /**
   * The date that is considered today, in ISO-8601 format. Presets resolve
   * relative to this; if unset, the user's current local date is used.
   */
  @property({ reflect: true }) today?: string

  /**
   * Which day is considered the first day of the week. `0` for Sunday, `1` for
   * Monday, etc. Default is Monday.
   */
  @property({ reflect: true, attribute: 'first-day-of-week', type: Number }) firstDayOfWeek: DaysOfWeek
    = DaysOfWeek.Monday

  /**
   * Displays the ISO-8601 week number of each visible week in a leading column
   * of the calendar. Forwarded to `nord-calendar`, so both visible months are
   * numbered. Adds roughly one day cell of width per month.
   */
  @property({ reflect: true, type: Boolean, attribute: 'week-numbers' }) weekNumbers = false

  /**
   * Controls which days are disabled and therefore disallowed.
   * For example, this can be used to disallow selection of weekends.
   */
  @property({ attribute: false }) isDateDisabled: DatePredicate = isDateDisabled

  /**
   * Controls which days are highlighted with a small indicator.
   * Returning `false` will not show an indicator.
   * Returning `true` will show the indicator, but without an accessible label. Therefore
   * Returning a string will show the indicator, and use the string as accessible label.
   * It is recommended to return a string rather than `true` whenever possible.
   */
  @property({ attribute: false }) isDateHighlighted: (date: Date) => string | boolean = isDateHighlighted

  /**
   * Hides the built-in list of preset ranges, leaving only the calendar.
   */
  @property({ reflect: true, type: Boolean, attribute: 'hide-presets' }) hidePresets = false

  /**
   * Restricts the preset list to this subset of preset ids (in their natural
   * group order). When unset, the full catalogue is shown. Ids:
   * `today`, `yesterday`, `last-7`/`last-30`/`last-60`/`last-90`/`last-365`,
   * `last-week`, `last-month`, `last-6-months`, `last-quarter`, `last-year`,
   * `last-12-months`, `current-week`/`current-month`/`current-quarter`/`current-year`,
   * and quarter ids `q-<year>-<1-4>`.
   */
  @property({ attribute: false }) presets?: string[]

  /**
   * Render the picker inline — the presets, calendar and Apply/Cancel/Clear are
   * always visible, with no trigger button or popout. Use it when the picker
   * lives directly on the page (e.g. in a form) rather than behind a trigger.
   */
  @property({ reflect: true, type: Boolean }) inline = false

  /**
   * The name used to submit the **start** date as its own form field. When both
   * `start-name` and `end-name` are set, the picker submits two fields
   * (`start-name`=startDate, `end-name`=endDate) instead of the single `name`.
   */
  @property({ reflect: true, attribute: 'start-name' }) startName?: string

  /**
   * The name used to submit the **end** date as its own form field. See
   * `start-name`.
   */
  @property({ reflect: true, attribute: 'end-name' }) endName?: string

  /**
   * Validation error message, set automatically when the committed range falls
   * outside min/max.
   */
  @property({ attribute: false }) internalError?: string

  /** The pending range inside the popout, before Apply commits it. */
  @state() private pendingStart = ''
  @state() private pendingEnd = ''

  /** Whether the popout shows a single month (narrow) rather than two. */
  @state() private isNarrow = false

  /** Whether the collapsed preset dropdown (narrow layout) is open. */
  @state() private presetsOpen = false

  /**
   * Below this viewport width (in `rem`) the picker collapses to a single month
   * with stacked presets. Measured against the viewport — NOT the host — because
   * the popout floats (`position: fixed`) and is free to be as wide as the
   * viewport allows, regardless of the trigger's own width. A `resize` listener
   * is required (rather than a CSS media query) because the month count is a
   * `nord-calendar` prop in shadow DOM that CSS cannot reach.
   *
   * Two months + presets need ~900px to render without overflowing, so the
   * threshold sits a little above that. Expressed in `rem` (resolved against the
   * root font size on each measurement) so it scales with zoom / font size, the
   * same way the calendar's own `rem`-based sizing does.
   */
  private static readonly narrowThresholdRem = 57.5

  // Keeps the collapsed-presets popout width in sync with the trigger as the host
  // resizes; the narrow/wide decision itself tracks the viewport (see below).
  private resizeObserver = new ResizeObserver(() => {
    this.measureNarrow()
    this.syncPresetsInlineSize()
  })

  private handleViewportResize = () => {
    this.measureNarrow()
  }

  /**
   * Sets `isNarrow` from the viewport width. The `resize` listener drives this for
   * live changes, but it fires asynchronously, so it's also called synchronously
   * before opening the popout so the first render already shows the right number
   * of months. Ignores 0-width measurements (detached/hidden).
   */
  private measureNarrow(width: number = window.innerWidth) {
    if (width > 0) {
      const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
      this.isNarrow = width < DateRangePicker.narrowThresholdRem * rootFontSize
    }
  }

  /**
   * Mirror the collapsed-presets trigger width into a custom property so the
   * presets popout matches it (the popout floats `position: fixed`, so a `%`
   * width can't reference the trigger). Mirrors the combobox dropdown approach.
   */
  private syncPresetsInlineSize() {
    const collapsed = this.renderRoot.querySelector('.n-drp-presets-collapsed')
    if (collapsed) {
      this.style.setProperty('--_n-drp-presets-inline-size', `${collapsed.clientWidth}px`)
    }
  }

  /**
   * A user-supplied trigger element (`slot="trigger"`), captured from the light
   * DOM before the first render so it can be re-inserted into the rendered
   * structure. When present it replaces the built-in button; `null` uses the
   * default trigger.
   */
  private customTrigger: HTMLElement | null = null

  /** The interval string was the source of the most recent sync (avoids loops). */
  private syncingFromValue = false

  get valueAsDateRange(): [Date, Date] | undefined {
    const start = parseISODate(this.startDate)
    const end = parseISODate(this.endDate)
    return start && end ? [start, end] : undefined
  }

  /**
   * Get/set the committed range as a `[Date, Date]` tuple. Setting `undefined`
   * clears the range.
   */
  set valueAsDateRange(range: [Date, Date] | undefined) {
    if (!range) {
      this.value = ''
      return
    }
    const [start, end] = range
    this.value = joinInterval(printISODate(start), printISODate(end))
  }

  /** The form the `formdata` listener is currently attached to, if any. */
  private boundForm: HTMLFormElement | null = null

  connectedCallback() {
    super.connectedCallback()
    injectLegacyLightDomStyles(this, style)
    this.syncFormListener()
    this.resizeObserver.observe(this)
    window.addEventListener('resize', this.handleViewportResize)
    // Capture a custom trigger (`slot="trigger"`) before the first render owns
    // the light DOM; it is re-inserted into the rendered structure by `render`.
    this.customTrigger = this.querySelector<HTMLElement>(':scope > [slot="trigger"]')
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.boundForm?.removeEventListener('formdata', this.handleFormData)
    this.boundForm = null
    this.resizeObserver.disconnect()
    window.removeEventListener('resize', this.handleViewportResize)
  }

  // Render in the light DOM so the control participates in the document tree.
  protected createRenderRoot() {
    return this
  }

  // The control's own invalid state for the field context: a built-in/internal error.
  protected override get ownInvalid() {
    return Boolean(this.error || this.internalError) || super.ownInvalid
  }

  protected firstUpdated(changed: PropertyValues) {
    super.firstUpdated(changed)
    // Seed the inline pending selection from an initial value.
    if (this.inline) {
      this.pendingStart = this.startDate
      this.pendingEnd = this.endDate
    }
  }

  protected updated(changed: PropertyValues) {
    super.updated(changed)
    // The associated form can change after `form` is set or on reconnection.
    this.syncFormListener()
    this.wireCustomTrigger()
    // Keep the presets popout matched to the (re-rendered) collapsed trigger.
    this.syncPresetsInlineSize()
  }

  /**
   * Give a slotted custom trigger the same ARIA wiring the built-in button has,
   * so `nord-popout` auto-attaches its open/close click via `aria-controls` and
   * assistive tech sees the dialog relationship and expanded state.
   */
  private wireCustomTrigger() {
    const trigger = this.customTrigger
    if (!trigger) {
      return
    }
    const popoutId = `${this.dateRangePickerId}-popout`
    trigger.setAttribute('aria-controls', popoutId)
    trigger.setAttribute('aria-haspopup', 'dialog')
    trigger.setAttribute('aria-expanded', this.open ? 'true' : 'false')
    // Drive `open` (the source of truth) from the trigger click, like the
    // built-in button's `@click`. `nord-popout` also auto-wires a click via
    // `aria-controls`, but show()/hide() are idempotent so the two coincide
    // harmlessly. The listener is added once (it's a no-op if already bound).
    trigger.removeEventListener('click', this.handleTriggerClick)
    trigger.addEventListener('click', this.handleTriggerClick)
  }

  /**
   * The control submits one OR two form fields (single interval, or separate
   * start/end), which `FormAssociatedMixin`'s single `FormDataController` can't
   * express, so listen for `formdata` directly and (re)bind to the active form.
   */
  private syncFormListener() {
    const form = this.form
    if (form === this.boundForm) {
      return
    }
    this.boundForm?.removeEventListener('formdata', this.handleFormData)
    this.boundForm = form
    this.boundForm?.addEventListener('formdata', this.handleFormData)
  }

  /**
   * Keep `value`, `start-date` and `end-date` consistent. The interval string is
   * the source of truth; whichever changed last drives the others.
   */
  @observe('value')
  protected handleValueObserve() {
    if (this.syncingFromValue) {
      return
    }
    this.syncingFromValue = true
    const [start, end] = splitInterval(this.value)
    this.startDate = start
    this.endDate = end
    this.internalError = this.validateBounds(start, end)
    this.syncingFromValue = false

    // Inline has no popout-open to seed the pending selection, so mirror the
    // committed value into it whenever it changes.
    if (this.inline) {
      this.pendingStart = start
      this.pendingEnd = end
    }
  }

  @observe('startDate')
  @observe('endDate')
  protected handlePartObserve() {
    if (this.syncingFromValue) {
      return
    }
    this.syncingFromValue = true
    this.value = joinInterval(this.startDate, this.endDate)
    this.internalError = this.validateBounds(this.startDate, this.endDate)
    this.syncingFromValue = false
  }

  /**
   * Suppress the inherited `FormDataController`. This control submits one OR two
   * fields depending on `start-name`/`end-name`, which a single controller can't
   * express, so `handleFormData` owns the entire submission instead. Returning
   * `undefined` here keeps the base controller from also appending `name`.
   */
  protected get formValue(): string | undefined {
    return undefined
  }

  private handleFormData = (e: FormDataEvent) => {
    if (this.disabled) {
      return
    }

    // Two-field mode: submit start and end under their own names.
    if (this.startName && this.endName) {
      if (this.startDate) {
        e.formData.append(this.startName, this.startDate)
      }
      if (this.endDate) {
        e.formData.append(this.endName, this.endDate)
      }
      return
    }

    // Single-field mode: submit the interval string under `name`.
    if (this.name && this.value) {
      e.formData.append(this.name, this.value)
    }
  }

  /**
   * The full catalogue of presets, resolved against the configured (or current)
   * "today". Date arithmetic mirrors NFE's `getPredefinedDateRange`: "last N
   * days" counts back `N` days from today; "last N months" are full calendar
   * months ending at the previous month-end; period/quarter presets use calendar
   * boundaries (first-day-of-week aware for weeks).
   */
  private get presetCatalogue(): Preset[] {
    const t = this.localize
    const fdow = this.firstDayOfWeek
    const currentYear = this.getToday().getFullYear()
    const lastYear = currentYear - 1

    const presets: Preset[] = [
      // today / yesterday
      { id: 'today', group: 'today', label: t.term('presetToday'), resolve: today => [today, today] },
      { id: 'yesterday', group: 'today', label: t.term('presetYesterday'), resolve: (today) => {
        const y = addDays(today, -1)
        return [y, y]
      } },

      // last N days (start = today − N, mirroring NFE)
      { id: 'last-7', group: 'lastDays', label: t.term('presetLastDays', 7), resolve: today => [addDays(today, -7), today] },
      { id: 'last-30', group: 'lastDays', label: t.term('presetLastDays', 30), resolve: today => [addDays(today, -30), today] },
      { id: 'last-60', group: 'lastDays', label: t.term('presetLastDays', 60), resolve: today => [addDays(today, -60), today] },
      { id: 'last-90', group: 'lastDays', label: t.term('presetLastDays', 90), resolve: today => [addDays(today, -90), today] },
      { id: 'last-365', group: 'lastDays', label: t.term('presetLastDays', 365), resolve: today => [addDays(today, -365), today] },

      // last calendar periods
      { id: 'last-week', group: 'lastPeriod', label: t.term('presetLastWeek'), resolve: (today) => {
        const thisWeekStart = startOfWeek(today, fdow)
        return [addDays(thisWeekStart, -7), addDays(thisWeekStart, -1)]
      } },
      { id: 'last-month', group: 'lastPeriod', label: t.term('presetLastMonth'), resolve: (today) => {
        const lastMonthEnd = addDays(startOfMonth(today), -1)
        return [startOfMonth(lastMonthEnd), lastMonthEnd]
      } },
      { id: 'last-6-months', group: 'lastPeriod', label: t.term('presetLastMonths', 6), resolve: (today) => {
        const end = addDays(startOfMonth(today), -1)
        return [startOfMonth(addMonths(end, -5)), end]
      } },
      { id: 'last-quarter', group: 'lastPeriod', label: t.term('presetLastQuarter'), resolve: (today) => {
        const lastQuarterEnd = addDays(startOfQuarter(today), -1)
        return [startOfQuarter(lastQuarterEnd), lastQuarterEnd]
      } },
      { id: 'last-year', group: 'lastPeriod', label: t.term('presetLastYear'), resolve: (today) => {
        const prev = setYear(today, today.getFullYear() - 1)
        return [startOfYear(prev), addDays(startOfYear(today), -1)]
      } },
      { id: 'last-12-months', group: 'lastPeriod', label: t.term('presetLastMonths', 12), resolve: (today) => {
        const end = addDays(startOfMonth(today), -1)
        return [startOfMonth(addMonths(end, -11)), end]
      } },

      // current calendar periods (start of period → today)
      { id: 'current-week', group: 'currentPeriod', label: t.term('presetCurrentWeek'), resolve: today => [startOfWeek(today, fdow), today] },
      { id: 'current-month', group: 'currentPeriod', label: t.term('presetCurrentMonth'), resolve: today => [startOfMonth(today), today] },
      { id: 'current-quarter', group: 'currentPeriod', label: t.term('presetCurrentQuarter'), resolve: today => [startOfQuarter(today), today] },
      { id: 'current-year', group: 'currentPeriod', label: t.term('presetCurrentYear'), resolve: today => [startOfYear(today), today] },
    ]

    // Explicit calendar quarters: Q4→Q1 of the current year (only those already
    // completed), then Q4→Q1 of last year.
    for (const year of [currentYear, lastYear]) {
      for (const quarter of [4, 3, 2, 1]) {
        presets.push({
          id: `q-${year}-${quarter}`,
          group: 'quarters',
          label: t.term('presetQuarter', quarter, year),
          resolve: () => getQuarterRange(quarter, year),
          available: year === currentYear ? today => isQuarterCompleted(quarter, today) : undefined,
        })
      }
    }

    return presets
  }

  /** The presets that should be shown, after subset/availability/min-max filters. */
  private get visiblePresets(): Preset[] {
    const today = this.getToday()
    const min = parseISODate(this.min || '')
    const max = parseISODate(this.max || '')

    let presets = this.presetCatalogue

    // Optional consumer-chosen subset, preserving catalogue order.
    if (this.presets) {
      const allowed = new Set(this.presets)
      presets = presets.filter(preset => allowed.has(preset.id))
    }

    return presets.filter((preset) => {
      if (preset.available && !preset.available(today)) {
        return false
      }
      // Drop presets whose resolved range falls outside min/max.
      const [start, end] = preset.resolve(today)
      if (min && start.getTime() < min.getTime()) {
        return false
      }
      if (max && end.getTime() > max.getTime()) {
        return false
      }
      return true
    })
  }

  /** The visible presets bucketed into their divider groups, in group order. */
  private get presetGroups(): Preset[][] {
    const visible = this.visiblePresets
    return PRESET_GROUP_ORDER
      .map(group => visible.filter(preset => preset.group === group))
      .filter(group => group.length > 0)
  }

  private getToday(): Date {
    return parseISODate(this.today || '') || new Date()
  }

  /** Which preset (if any) the pending selection currently matches. */
  private get activePresetId(): string | undefined {
    if (!this.pendingStart || !this.pendingEnd) {
      return undefined
    }
    const today = this.getToday()
    for (const preset of this.visiblePresets) {
      const [start, end] = preset.resolve(today)
      if (printISODate(start) === this.pendingStart && printISODate(end) === this.pendingEnd) {
        return preset.id
      }
    }
    return undefined
  }

  private createDateFormatters() {
    this.dateFormat = new Intl.DateTimeFormat(this.localize.resolvedLang, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  /** The text shown in the trigger: the formatted range, or the placeholder. */
  private get displayValue(): string {
    const range = this.valueAsDateRange
    if (!range) {
      return ''
    }
    const [start, end] = range
    const sep = this.localize.term('rangeSeparator')
    return `${this.dateFormat.format(start)} ${sep} ${this.dateFormat.format(end)}`
  }

  /** The in-progress (pending) range formatted for the footer, or '' while empty. */
  private get pendingRangeLabel(): string {
    const start = parseISODate(this.pendingStart)
    if (!start) {
      return ''
    }
    const end = parseISODate(this.pendingEnd)
    const sep = this.localize.term('rangeSeparator')
    return end ? `${this.dateFormat.format(start)} ${sep} ${this.dateFormat.format(end)}` : this.dateFormat.format(start)
  }

  private validateBounds(start: string, end: string): string | undefined {
    const minDate = parseISODate(this.min || '')
    const maxDate = parseISODate(this.max || '')
    const startDate = parseISODate(start)
    const endDate = parseISODate(end)

    if (minDate && startDate && startDate < minDate) {
      return this.localize.term('dateOutOfMinBound', this.min!)
    }
    if (maxDate && endDate && endDate > maxDate) {
      return this.localize.term('dateOutOfMaxBound', this.max!)
    }
    return undefined
  }

  render() {
    const display = this.displayValue
    const placeholder = this.placeholder || this.localize.term('placeholder')
    const anchorId = `${this.dateRangePickerId}-anchor`
    const popoutId = `${this.dateRangePickerId}-popout`
    const headerId = `${this.dateRangePickerId}-header`
    // Naming resolved by the FieldControlMixin getters (field context in a field,
    // built-in label standalone, or host aria-* fallback). Light DOM, so the
    // aria-labelledby IDREF resolves.
    const ariaLabel = this.controlAriaLabel
    const ariaLabelledby = this.controlAriaLabelledby
    const ariaDescribedby = this.controlAriaDescribedby
    const activePreset = this.activePresetId

    // Inline: render the presets + calendar + actions directly, with no trigger
    // or popout.
    if (this.inline) {
      return html`
        ${this.renderLabel()}
        <div
          class="n-drp"
          id=${anchorId}
          aria-label=${ifDefined(ariaLabel)}
          aria-labelledby=${ifDefined(ariaLabelledby)}
          aria-describedby=${ifDefined(ariaDescribedby)}
          ?inert=${this.effectiveDisabled || this.readonly}
          aria-disabled=${this.effectiveDisabled ? 'true' : nothing}
        >
          ${this.renderBody(activePreset)}
        </div>
        ${this.renderError()}
        ${this.isHintBelow ? this.renderHint() : nothing}
      `
    }

    return html`
      <div class="n-drp" id=${anchorId}>
        ${this.customTrigger
          ? this.customTrigger
          : html`
              <nord-input
                class=${classMap({ 'n-drp-toggle': true, 'is-placeholder': !display })}
                type="button"
                size=${this.size}
                id=${`${this.dateRangePickerId}-input`}
                value=${display || placeholder}
                label=${this.insideField ? nothing : ifDefined(this.label)}
                hint=${this.insideField ? nothing : ifDefined(this.hint)}
                error=${this.insideField ? nothing : cond(this.error || this.internalError, this.error || this.internalError)}
                placeholder=${ifDefined(placeholder)}
                ?disabled=${this.effectiveDisabled}
                ?required=${this.required}
                ?readonly=${this.readonly}
                ?hint-below=${this.hintBelow}
                ?hide-label=${this.insideField || this.hideLabel}
                aria-haspopup="dialog"
                aria-controls=${popoutId}
                aria-expanded=${this.open ? 'true' : 'false'}
                aria-label=${ifDefined(ariaLabel)}
                aria-describedby=${ifDefined(ariaDescribedby)}
                aria-invalid=${ifDefined(this.controlAriaInvalid)}
                @click=${this.handleTriggerClick}
                @focus=${this.handleFocus}
                @blur=${this.handleBlur}
                ${ref(this.focusableRef)}
              >
                ${this.insideField || this.hintSlot.isEmpty ? nothing : html`<slot name="hint" slot="hint"></slot>`}
                ${this.insideField || this.labelSlot.isEmpty ? nothing : html`<slot name="label" slot="label"></slot>`}
                <nord-button
                  slot="end"
                  class="n-drp-toggle-button"
                  size=${this.size}
                  ?disabled=${this.effectiveDisabled || this.readonly}
                  aria-controls=${popoutId}
                  type="button"
                >
                  <nord-icon name="interface-calendar"></nord-icon>
                  <nord-visually-hidden>${this.localize.term('buttonLabel')}</nord-visually-hidden>
                </nord-button>
              </nord-input>
            `}

        <nord-popout
          id=${popoutId}
          controlled
          anchor=${anchorId}
          align="start"
          position="block-end"
          role="dialog"
          aria-modal="true"
          aria-labelledby=${headerId}
          @open=${this.openState.onPopoutOpen}
          @close=${this.openState.onPopoutClose}
        >
          <div class="n-drp-popout">
            <div class="n-drp-popout-header">
              <span class="n-drp-heading" id=${headerId}>${this.localize.term('modalHeading')}</span>
              <button
                type="button"
                class="n-drp-close"
                aria-label=${this.localize.term('closeLabel')}
                @click=${() => this.hide(true)}
              >
                <nord-icon name="interface-close-small"></nord-icon>
              </button>
            </div>

            ${this.renderBody(activePreset)}
          </div>
        </nord-popout>
      </div>
    `
  }

  /** The presets + calendar + actions shared by the popout and inline layouts. */
  private renderBody(activePreset: string | undefined) {
    const actionSize = this.isNarrow ? 's' : 'm'

    return html`
      <div class=${classMap({ 'n-drp-body': true, 'is-narrow': this.isNarrow })}>
        ${this.hidePresets || this.presetGroups.length === 0
          ? nothing
          : this.isNarrow
            ? this.renderPresetsCollapsed(activePreset)
            : this.renderPresetsList(activePreset)}

        <div class="n-drp-calendar">
          <nord-calendar
            range
            expand
            months=${this.isNarrow ? 1 : 2}
            range-start=${this.pendingStart}
            range-end=${this.pendingEnd}
            min=${ifDefined(this.min)}
            max=${ifDefined(this.max)}
            today=${ifDefined(this.today)}
            ?week-numbers=${this.weekNumbers}
            .firstDayOfWeek=${this.firstDayOfWeek}
            .isDateDisabled=${this.isDateDisabled}
            .isDateHighlighted=${this.isDateHighlighted}
            @range-change=${this.handleRangeChange}
          ></nord-calendar>
        </div>
      </div>

      <div class="n-drp-footer">
        <nord-button type="button" size=${actionSize} variant="plain" @click=${this.handleClear}>
          ${this.localize.term('clearLabel')}
        </nord-button>
        <div class="n-drp-footer-end">
          ${this.pendingRangeLabel ? html`<span class="n-drp-period">${this.pendingRangeLabel}</span>` : nothing}
          ${this.inline
            ? nothing
            : html`
                <nord-button type="button" size=${actionSize} @click=${() => this.hide(true)}>
                  ${this.localize.term('cancelLabel')}
                </nord-button>
              `}
          <nord-button
            type="button"
            size=${actionSize}
            variant="primary"
            ?disabled=${!this.pendingStart || !this.pendingEnd}
            @click=${this.handleApply}
          >
            ${this.localize.term('applyLabel')}
          </nord-button>
        </div>
      </div>
    `
  }

  /** A single preset row. */
  private renderPreset(preset: Preset, activePreset: string | undefined) {
    return html`
      <li>
        <button
          type="button"
          class=${classMap({ 'n-drp-preset': true, 'is-active': preset.id === activePreset })}
          aria-pressed=${preset.id === activePreset ? 'true' : 'false'}
          @click=${() => this.handlePresetClick(preset)}
        >
          ${preset.label}
        </button>
      </li>
    `
  }

  /** The presets bucketed into one `<ul>` per divider group. */
  private renderPresetGroups(activePreset: string | undefined) {
    return this.presetGroups.map(
      group => html`
        <ul class="n-drp-preset-group">
          ${group.map(preset => this.renderPreset(preset, activePreset))}
        </ul>
      `,
    )
  }

  /** Wide layout: the grouped presets shown as a list beside the calendar. */
  private renderPresetsList(activePreset: string | undefined) {
    return html`
      <div class="n-drp-presets" role="group" aria-label=${this.localize.term('presetsLabel')}>
        ${this.renderPresetGroups(activePreset)}
      </div>
    `
  }

  /** Narrow layout: the presets collapse into a dropdown button + popout. */
  private renderPresetsCollapsed(activePreset: string | undefined) {
    const popoutId = `${this.dateRangePickerId}-presets`
    const activeLabel = this.visiblePresets.find(preset => preset.id === activePreset)?.label

    return html`
      <div class="n-drp-presets-collapsed">
        <nord-button
          type="button"
          size="s"
          expand
          aria-haspopup="true"
          aria-controls=${popoutId}
          aria-expanded=${this.presetsOpen ? 'true' : 'false'}
        >
          ${activeLabel ?? this.localize.term('presetsCollapsedLabel')}
          <nord-icon slot="end" name="interface-dropdown-small"></nord-icon>
        </nord-button>

        <nord-popout
          id=${popoutId}
          always-floating
          expand
          position="block-end"
          ?open=${this.presetsOpen}
          @open=${() => (this.presetsOpen = true)}
          @close=${() => (this.presetsOpen = false)}
        >
          <div class="n-drp-presets-menu" role="group" aria-label=${this.localize.term('presetsLabel')}>
            ${this.renderPresetGroups(activePreset)}
          </div>
        </nord-popout>
      </div>
    `
  }

  private handleFocus = () => {
    this.dispatchEvent(new NordEvent('focus'))
  }

  private handleBlur = () => {
    this.dispatchEvent(new NordEvent('blur'))
  }

  private handlePresetClick(preset: Preset) {
    if (this.disabled || this.readonly) {
      return
    }
    const [start, end] = preset.resolve(this.getToday())
    this.pendingStart = printISODate(start)
    this.pendingEnd = printISODate(end)
    this.emitInput()
    // In the collapsed (narrow) layout the presets live in their own popout;
    // close it once a choice is made.
    this.presetsOpen = false
  }

  // The calendar owns the two-click range selection and hover preview across its
  // months; it fires `range-change` once a complete range is picked.
  private handleRangeChange = (e: DateRangeSelectEvent) => {
    e.stopPropagation()
    if (this.disabled || this.readonly) {
      return
    }
    this.pendingStart = printISODate(e.start)
    this.pendingEnd = printISODate(e.end)
    this.emitInput()
  }

  private emitInput() {
    this.dispatchEvent(
      new DateRangeChangeEvent('input', {
        value: joinInterval(this.pendingStart, this.pendingEnd),
        startDate: this.pendingStart,
        endDate: this.pendingEnd,
      }),
    )
  }

  private handleApply = () => {
    if (this.disabled || this.readonly || !this.pendingStart || !this.pendingEnd) {
      return
    }
    this.commit(this.pendingStart, this.pendingEnd)
    this.hide(true)
  }

  private handleClear = () => {
    if (this.disabled || this.readonly) {
      return
    }
    this.pendingStart = ''
    this.pendingEnd = ''
    this.commit('', '')
    this.hide(true)
  }

  /** Commits a range to `value`, syncs the parts, and fires `change`. */
  private commit(start: string, end: string) {
    this.value = joinInterval(start, end)
    this.dispatchEvent(
      new DateRangeChangeEvent('change', {
        value: this.value,
        startDate: start,
        endDate: end,
      }),
    )
  }

  // The trigger drives `open` (the source of truth); the OpenStateController then
  // drives the popout's show()/hide(), syncs `open` back from the popout's own
  // events, emits the single canonical open/close, and rejects opening while the
  // picker is disabled/read-only (including via the popout's auto-wired click on
  // a slotted custom trigger, which onPopoutOpen rejects when inert).
  private handleTriggerClick = () => {
    // Resolve narrow/wide synchronously so the popout opens with the right
    // number of months (the ResizeObserver fires too late for the first open).
    this.measureNarrow()
    this.openState.toggle()
  }

  /**
   * Open the picker programmatically.
   */
  show() {
    this.measureNarrow()
    this.openState.show()
  }

  /**
   * Close the picker programmatically.
   * @param moveFocusToToggle Move focus back to the trigger after closing.
   */
  hide(moveFocusToToggle = false) {
    // The controller always closes the popout with `hide(false)` (no focus
    // restore), so move focus back to the trigger here when asked — covering
    // both the built-in button and a slotted custom trigger.
    if (moveFocusToToggle) {
      (this.customTrigger ?? this.toggle)?.focus()
    }
    this.openState.hide()
  }

  /**
   * Move focus to the picker's trigger.
   */
  focus(options?: FocusOptions) {
    this.toggle?.focus(options)
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-date-range-picker': DateRangePicker
  }
}
